import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Field, Notice, Spinner } from '../../components/ui';
import { Dialog } from '../../components/Dialog';
import { ApiError } from '../../lib/api';
import { relativeDate } from '../../lib/school';
import { createFramework, getLibrary, type LibraryRow } from '../../lib/frameworks';
import { FrameworkTabs } from './FrameworkTabs';
import { Pager } from '../../components/Pager';
import '../Page.css';
import './Admin.css';

const PAGE_SIZE = 25;

const STATUS: Record<LibraryRow['status'], { label: string; accent?: boolean }> = {
  draft: { label: 'Draft' },
  pending: { label: 'Sent for review', accent: true },
  approved: { label: 'Published', accent: true },
  rejected: { label: 'Rejected' },
};

/** The framework library: build new ones, and see who has each. */
export function AdminFrameworksPage() {
  const navigate = useNavigate();
  const library = useQuery({ queryKey: ['admin-frameworks'], queryFn: getLibrary });
  const [q, setQ] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows = (library.data ?? []).filter((f) => !q || `${f.name} ${f.owner_name ?? ''}`.toLowerCase().includes(q.toLowerCase()));
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [q]);
  const shown = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { id } = await createFramework(name.trim(), description.trim());
      navigate(`/admin/frameworks/${id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn’t work. Try again.');
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <header className="admin-head">
        <div className="page-head">
          <p className="eyebrow">EducAitor admin</p>
          <h1>Framework library</h1>
          <p className="muted">Build frameworks, publish them, and give organisations access. What leaders have asked for is under Requests.</p>
        </div>
        <Button onClick={() => setNewOpen(true)}>New framework</Button>
      </header>
      <FrameworkTabs />
      <div className="search">
        <Field id="admin-fw-search" label="Search" placeholder="Framework or organisation" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {library.isLoading ? (
        <Spinner />
      ) : library.isError ? (
        <Notice tone="error">Couldn&rsquo;t load frameworks.</Notice>
      ) : rows.length === 0 ? (
        <div className="panel muted">{q ? `No framework matches “${q}”.` : 'No frameworks yet.'}</div>
      ) : (
        <>
          <div className="panel table-wrap">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>Framework</th>
                  <th>Status</th>
                  <th>Used by</th>
                  <th>Updated</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {shown.map((f) => {
                  const usedBy = Number(f.used_by ?? Number(f.shared_with) + (f.school_id ? 1 : 0));
                  const defaults = Number(f.default_for);
                  return (
                    <tr key={f.id} className="clickable" onClick={() => navigate(`/admin/frameworks/${f.id}`)}>
                      <td>
                        <span className="cell-main">{f.name}</span>
                        <span className="cell-sub">
                          {Number(f.domains)} {Number(f.domains) === 1 ? 'area' : 'areas'} · {Number(f.indicators)}{' '}
                          {Number(f.indicators) === 1 ? 'indicator' : 'indicators'} ·{' '}
                          {f.school_id ? `Made for ${f.owner_name ?? 'an organisation'}` : Number(f.listed) ? 'Offered to everyone' : 'Assigned only'}
                        </span>
                      </td>
                      <td>
                        <span className={`chip${STATUS[f.status].accent ? ' chip-accent' : ''}`}>{STATUS[f.status].label}</span>
                      </td>
                      <td data-label="Used by">
                        {usedBy ? (
                          <span className="cell-main">
                            {usedBy} {usedBy === 1 ? 'organisation' : 'organisations'}
                          </span>
                        ) : (
                          <span className="faint">Not assigned yet</span>
                        )}
                        {defaults ? <span className="cell-sub">{defaults === usedBy ? 'All use it as their default' : `${defaults} as their default`}</span> : null}
                      </td>
                      <td data-label="Updated">{relativeDate(f.updated_at)}</td>
                      <td className="row-go" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24">
                          <path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager page={page} pageSize={PAGE_SIZE} total={rows.length} onPage={setPage} noun="frameworks" />
        </>
      )}

      <Dialog open={newOpen} onClose={() => !busy && setNewOpen(false)} title="New framework">
        <p className="muted">It starts as a draft that no one else can see. Add its areas and indicators, then publish it.</p>
        <Field id="fw-new-name" label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <Field id="fw-new-desc" label="Short description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
        {error ? <Notice tone="error">{error}</Notice> : null}
        <div className="dialog-actions">
          <Button variant="ghost" onClick={() => setNewOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={create} loading={busy} disabled={!name.trim()}>
            Create and start building
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
